import {DataModificationsHeader} from 'audit/DataModifications';
import {MixtureGroup} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import {RegistrationDAOResponseBase} from 'notebook/integrations/registry/RegistrationActionUtil';
import {clientId} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {getLatestVersionId} from 'notebook/structured-tables/ActionFunctionDAO';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import * as net from 'util/Net';

const enum ACTION_FUNCTIONS {
  MIXTURE_PREP_SUBMIT = 'mixture-prep-submit',
  RESYNC = 'resync',
}

function _runAction<ActionResponse>(
  entryId: string,
  provenanceUuid: string,
  actionName: string,
  evaluatedTableMap: {[tableName: string]: Array<Array<EvaluatedCell>>},
  actionFunctionContext: ActionFunctionContext,
  extraParams?: object,
  dataModsHeader?: DataModificationsHeader,
  latestVersionId?: number
): Promise<ActionResponse> {
  const resolvedLatestVersionId = latestVersionId ?? getLatestVersionId(entryId);
  return net.postJSONPromise({
    url: `/1/api/entries/${entryId}/note-linked-groups/${provenanceUuid}/action/${actionName}`,
    data: {
      evaluatedTableMap,
      actionFunctionContext,
      clientId,
      latestVersionId: resolvedLatestVersionId,
      ...extraParams,
    },
    extraHeaders: dataModsHeader,
  });
}

export interface ResyncGroupResponse {
  entryVersionId: number;
}

/**
 * Invokes the server-side re-sync action function for a note-linked group (gated behind
 * ELN_RESYNC_USE_BACKEND_ACTION_FUNCTION). Server-side equivalent of refreshing every structured table in
 * the group with its row updater.
 */
export function resyncNoteLinkedGroup(
  entryId: string,
  provenanceUuid: string,
  evaluatedTableMap: {[tableName: string]: Array<Array<EvaluatedCell>>},
  actionFunctionContext: ActionFunctionContext
): Promise<ResyncGroupResponse> {
  return _runAction<ResyncGroupResponse>(
    entryId,
    provenanceUuid,
    ACTION_FUNCTIONS.RESYNC,
    evaluatedTableMap,
    actionFunctionContext
  );
}

export interface MixtureGroupResponse extends RegistrationDAOResponseBase {}

export function submitMixtureGroup(
  entryId: string,
  mixtureGroup: MixtureGroup,
  evaluatedTableMap: {[tableName: string]: Array<Array<EvaluatedCell>>},
  actionFunctionContext: ActionFunctionContext,
  extraParams: {allowMerging: boolean} = {allowMerging: false},
  dataModsHeader?: DataModificationsHeader,
  latestVersionId?: number
): Promise<MixtureGroupResponse> {
  return _runAction<MixtureGroupResponse>(
    entryId,
    mixtureGroup.provenanceUuid,
    ACTION_FUNCTIONS.MIXTURE_PREP_SUBMIT,
    evaluatedTableMap,
    actionFunctionContext,
    extraParams,
    dataModsHeader,
    latestVersionId
  );
}
